/**
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

enum RuleOperator {
  EQ = 'eq',
  NOEQ = 'noeq',
  IN = 'in',
  ALLIN = 'allin',
  ANYIN = 'anyin',
  NOANYIN = 'noanyin',
  NOIN = 'noin',
  LTE = 'lte',
  GTE = 'gte',
}

enum CompoundOperator {
  AND = 'AND',
  OR = 'OR',
}

class Rule {
  fact: string;
  operator: RuleOperator;
  value: any;

  constructor(factKey: string, operator: RuleOperator, value: any) {
    if (typeof factKey !== 'string') {
      throw new Error('Fact key is required to be a string');
    }
    this.fact = factKey;
    this.operator = operator;
    this.value = value;
  }

  public evaluate(factValue: any): boolean {
    switch (this.operator) {
      case RuleOperator.EQ:
        return factValue === this.value;
      case RuleOperator.NOEQ:
        return factValue !== this.value;
      case RuleOperator.IN:
        if (typeof factValue == 'string') {
          return factValue.includes(this.value);
        }
        return (
          Array.isArray(factValue) &&
          this.evaluateArrayCondition(this.value, factValue, Array.isArray(this.value))
        );

      case RuleOperator.ALLIN:
        return Array.isArray(factValue) && this.evaluateArrayCondition(this.value, factValue, true);

      case RuleOperator.ANYIN:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return this.value.some((element) => factValue.includes(element));
        }
        return false;

      case RuleOperator.NOANYIN:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return !this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return !this.value.some((element) => factValue.includes(element));
        }
        return true;

      case RuleOperator.NOIN:
        if (typeof factValue == 'string') {
          return !factValue.includes(this.value);
        }
        return (
          Array.isArray(factValue) &&
          !this.evaluateArrayCondition(this.value, factValue, Array.isArray(this.value))
        );
      case RuleOperator.LTE:
        return this.evaluateComparison(this.value, factValue, (a, b) => a <= b);
      case RuleOperator.GTE:
        return this.evaluateComparison(this.value, factValue, (a, b) => a >= b);
      default:
        throw new Error(`Unsupported operator: ${this.operator}`);
    }
  }

  private evaluateArrayCondition(value: any, factValue: any[], matchAll: boolean = false): boolean {
    if (Array.isArray(value) && matchAll) {
      return value.every((element) => factValue.includes(element));
    } else if (Array.isArray(value) && !matchAll) {
      return value.some((element) => factValue.includes(element));
    }
    return factValue.includes(value);
  }

  /**
   * Handles expressions like '3/d', '10/w', etc.
   * The factValue should be an array of timestamps.
   */
  private evaluateComparison(
    value: string | number,
    factValue: any,
    comparator: (a: number, b: number) => boolean
  ): boolean {
    if (typeof value === 'number') {
      return comparator(value, factValue);
    }

    // Frequncy capping expressions like '3/d', '10/w', etc.
    const match = /^(\d+)\s*\/\s*([hdw])$/i.exec(value);
    if (!match || !Array.isArray(factValue)) return false;

    const limit = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    const now = Date.now();
    const windowStart = {
      h: now - 1 * 60 * 60 * 1000,
      d: now - 1 * 24 * 60 * 60 * 1000,
      w: now - 7 * 24 * 60 * 60 * 1000,
    }[unit];

    const recentViews = factValue.filter((ts: number) => ts >= (windowStart as number));

    return comparator(recentViews.length, limit);
  }
}

class CompoundRule {
  op: CompoundOperator;
  rules: (Rule | CompoundRule)[];

  constructor(operator: CompoundOperator, rules: (Rule | CompoundRule)[]) {
    if (rules.length < 2) {
      throw new Error('Compound rule must have at least 2 rules');
    }
    this.op = operator;
    this.rules = rules;
  }

  public evaluate(facts: { [key: string]: any }): boolean {
    switch (this.op) {
      case CompoundOperator.AND:
        return this.rules.every((rule) => this.evaluateRule(rule, facts));
      case CompoundOperator.OR:
        return this.rules.some((rule) => this.evaluateRule(rule, facts));
      default:
        throw new Error(`Unsupported compound operator: ${this.op}`);
    }
  }

  private evaluateRule(rule: Rule | CompoundRule, facts: { [key: string]: any }): boolean {
    if (rule instanceof Rule) {
      return rule.evaluate(facts[rule.fact]);
    } else if (rule instanceof CompoundRule) {
      return rule.evaluate(facts);
    }
    throw new Error('Invalid rule type');
  }
}

type RuleNode = Rule | CompoundRule;

export { CompoundOperator, CompoundRule, Rule, RuleOperator };
export type { RuleNode };
